import { useState, useEffect, useRef, useCallback } from "react";
import { QrCode, Users, Copy, X, ExternalLink, Wifi, WifiOff, Share2, Minus, Settings, Languages, Zap } from "lucide-react";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PresenterLanguagesSelector, AVAILABLE_LANGUAGES } from "./PresenterLanguagesSelector";
import { useLanguage } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function TranslationRoomCard({ 
  isPro, 
  sourceLang, 
  targetLang,
  currentTranslation,
  originalText,
  detectedSourceLang, // Actual language detected by Whisper
  isRecording,
  onPresenterLanguagesChange  // Callback to notify parent when room is active with languages
}) {
  const { t } = useLanguage();
  const [room, setRoom] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [presenterLanguages, setPresenterLanguages] = useState([sourceLang || "pt"]);
  
  const wsRef = useRef(null);
  const lastTranslationRef = useRef("");
  const roomRef = useRef(null);
  useEffect(() => { roomRef.current = room; }, [room]);

  const ACTIVE_ROOM_KEY = "activeTempRoom";

  // Get the base URL for sharing (use current domain)
  const getShareUrl = useCallback(() => {
    if (!room) return "";
    const baseUrl = window.location.origin;
    return `${baseUrl}/sala/${room.room_id}`;
  }, [room]);

  const reconnectAttemptsRef = useRef(0);
  const heartbeatIntervalRef = useRef(null);

  // HTTP Heartbeat - more reliable than WebSocket through proxies
  const sendHeartbeat = useCallback(async (roomId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/${roomId}/heartbeat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      const data = await response.json();
      if (data.success) {
        setViewerCount(data.viewer_count || 0);
        console.log("[ROOM] Heartbeat sent successfully, viewers:", data.viewer_count);
      } else {
        console.warn("[ROOM] Heartbeat failed:", data.error);
      }
      return data.success;
    } catch (e) {
      console.error("[ROOM] Heartbeat error:", e);
      return false;
    }
  }, []);

  // Start HTTP heartbeat interval
  const startHeartbeat = useCallback((roomId) => {
    // Clear any existing interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    // Send heartbeat immediately
    sendHeartbeat(roomId);
    
    // Then send every 30 seconds
    heartbeatIntervalRef.current = setInterval(() => {
      sendHeartbeat(roomId);
    }, 30000);
    
    console.log("[ROOM] Heartbeat interval started for room", roomId);
  }, [sendHeartbeat]);

  // Stop heartbeat interval
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
      console.log("[ROOM] Heartbeat interval stopped");
    }
  }, []);

  // Connect to room as presenter via WebSocket (optional, for real-time features)
  const connectAsPresenter = useCallback((roomId) => {
    const wsUrl = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/room/${roomId}/presenter`);
    
    ws.onopen = () => {
      console.log("[ROOM] WebSocket connected as presenter to room", roomId);
      setConnected(true);
      reconnectAttemptsRef.current = 0;
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "connected") {
          // Room info received
        } else if (data.type === "viewer_count") {
          setViewerCount(data.count);
        } else if (data.type === "pong" || data.type === "keepalive") {
          setViewerCount(data.viewer_count || 0);
        }
      } catch (e) {
        console.error("[ROOM] Error parsing message:", e);
      }
    };
    
    ws.onclose = (event) => {
      console.log("[ROOM] WebSocket disconnected, code:", event.code);
      setConnected(false);
      wsRef.current = null;
      
      // ALWAYS try to reconnect - room NEVER closes automatically
      // Only manual close (code 1000) should stop reconnection
      if (event.code !== 1000 && roomRef.current) {
        reconnectAttemptsRef.current++;
        const delay = Math.min(3000 * reconnectAttemptsRef.current, 15000); // Max 15 seconds
        console.log(`[ROOM] Reconnecting WebSocket in ${delay/1000}s (attempt ${reconnectAttemptsRef.current})...`);
        setTimeout(() => {
          if (roomRef.current) {
            connectAsPresenter(roomId);
          }
        }, delay);
      }
      // IMPORTANT: Room NEVER closes automatically - only when user clicks "Encerrar Sala"
    };
    
    ws.onerror = (error) => {
      console.error("[ROOM] WebSocket error:", error);
    };
    
    wsRef.current = ws;
  }, []);

  // Restore an active temporary room after page refresh
  useEffect(() => {
    if (!isPro) return;
    const saved = localStorage.getItem(ACTIVE_ROOM_KEY);
    if (!saved) return;
    let roomId;
    try { roomId = JSON.parse(saved).room_id; } catch { localStorage.removeItem(ACTIVE_ROOM_KEY); return; }
    if (!roomId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/rooms/${roomId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success && data.room?.is_active && !data.room.is_permanent) {
          setRoom(data.room);
          setViewerCount(data.room.viewer_count || 0);
          if (data.room.presenter_languages?.length) setPresenterLanguages(data.room.presenter_languages);
          startHeartbeat(roomId);
          connectAsPresenter(roomId);
          toast.success(`Sala ${roomId} restaurada`);
        } else {
          localStorage.removeItem(ACTIVE_ROOM_KEY);
        }
      } catch (e) {
        console.error("[ROOM] Failed to restore room:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [isPro, startHeartbeat, connectAsPresenter]);

  // Create a new room
  const createRoom = async () => {
    if (!isPro) {
      toast.error("Salas de Tradução são exclusivas para usuários PRO");
      return;
    }
    
    if (presenterLanguages.length === 0) {
      toast.error("Selecione pelo menos um idioma dos apresentadores");
      return;
    }
    
    setIsCreating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          presenter_name: "Apresentador",
          source_lang: sourceLang,
          target_lang: targetLang,
          presenter_languages: presenterLanguages
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setRoom(data.room);
        setViewerCount(0);
        localStorage.setItem(ACTIVE_ROOM_KEY, JSON.stringify({ room_id: data.room.room_id }));
        // Start HTTP heartbeat (main keep-alive mechanism)
        startHeartbeat(data.room.room_id);
        // Try WebSocket connection (optional, for real-time features)
        connectAsPresenter(data.room.room_id);
        toast.success(`Sala ${data.room.room_id} criada com sucesso!`);
        setShowQRModal(true);
        setShowConfigPanel(false);
      } else {
        toast.error("Erro ao criar sala");
      }
    } catch (error) {
      console.error("Error creating room:", error);
      toast.error("Erro ao criar sala");
    } finally {
      setIsCreating(false);
    }
  };

  // Close room
  const closeRoom = async () => {
    if (!room) return;
    
    try {
      // Stop heartbeat first
      stopHeartbeat();
      
      if (wsRef.current) {
        wsRef.current.close();
      }
      
      await fetch(`${BACKEND_URL}/api/rooms/${room.room_id}`, {
        method: "DELETE"
      });
      
      localStorage.removeItem(ACTIVE_ROOM_KEY);
      setRoom(null);
      setConnected(false);
      setViewerCount(0);
      toast.success("Sala encerrada");
    } catch (error) {
      console.error("Error closing room:", error);
    }
  };

  // Send translation updates to room - uses WebSocket if connected, HTTP as fallback
  const sendTranslationToRoom = useCallback(async (translation, original) => {
    if (!room || !translation) return;
    
    // Use original text if available, otherwise use translation as original
    const originalToSend = original || translation;
    
    // Use detected language if available, otherwise fall back to configured sourceLang
    const actualSourceLang = detectedSourceLang || sourceLang;
    
    // Try WebSocket first (faster)
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({
          type: "translation",
          text: translation,
          original: originalToSend,  // Send original for per-viewer translation
          source_lang: actualSourceLang,  // Use detected language, not configured
          target_lang: targetLang
        }));
        console.log("[ROOM] Translation sent via WebSocket with original:", originalToSend.substring(0, 50), "source_lang:", actualSourceLang);
        return;
      } catch (e) {
        console.warn("[ROOM] WebSocket send failed, falling back to HTTP:", e);
      }
    }
    
    // Fallback to HTTP (more reliable)
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/${room.room_id}/translation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: translation,
          original: originalToSend,  // Send original for per-viewer translation
          source_lang: actualSourceLang,  // Use detected language, not configured
          target_lang: targetLang
        })
      });
      const data = await response.json();
      if (data.success) {
        console.log("[ROOM] Translation sent via HTTP, viewers:", data.viewer_count, "source_lang:", actualSourceLang);
        setViewerCount(data.viewer_count || 0);
      }
    } catch (e) {
      console.error("[ROOM] Failed to send translation via HTTP:", e);
    }
  }, [room, sourceLang, targetLang, detectedSourceLang]);

  // Track original text changes
  const lastOriginalRef = useRef("");

  // Watch for translation changes and send to room
  useEffect(() => {
    if (!room || !currentTranslation) return;
    if (currentTranslation === lastTranslationRef.current && originalText === lastOriginalRef.current) return;
    
    lastTranslationRef.current = currentTranslation;
    lastOriginalRef.current = originalText || "";
    sendTranslationToRoom(currentTranslation, originalText);
  }, [currentTranslation, originalText, room, sendTranslationToRoom]);

  // Ping to keep connection alive - send every 15 seconds
  useEffect(() => {
    if (!connected || !wsRef.current) return;
    
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        console.log("[ROOM] Sending ping to keep connection alive");
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 15000);
    
    return () => clearInterval(interval);
  }, [connected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
      if (wsRef.current) {
        wsRef.current.close();
      }
      // Clear presenter languages when room closes
      if (onPresenterLanguagesChange) {
        onPresenterLanguagesChange([]);
      }
    };
  }, [stopHeartbeat, onPresenterLanguagesChange]);

  // Notify parent when room is active with presenter languages
  useEffect(() => {
    if (onPresenterLanguagesChange) {
      if (room && presenterLanguages.length > 0) {
        onPresenterLanguagesChange(presenterLanguages);
      } else if (!room) {
        onPresenterLanguagesChange([]);
      }
    }
  }, [room, presenterLanguages, onPresenterLanguagesChange]);

  // Copy link to clipboard
  const copyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    toast.success("Link copiado!");
  };

  // Share via native share API
  const shareRoom = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Tradução ao Vivo",
          text: `Acesse a tradução ao vivo - Sala ${room.room_id}`,
          url: getShareUrl()
        });
      } catch (e) {
        if (e.name !== "AbortError") {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  // Non-PRO user view
  if (!isPro) {
    return (
      <Card className="col-span-12 bento-card opacity-60" data-testid="translation-room-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <QrCode className="w-5 h-5" />
            {t("translationRooms")}
            <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
              PRO
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-zinc-400 text-sm">
            {t("upgradeToProRooms")}
          </p>
          
          <Link 
            to="/pricing" 
            className="block w-full text-center py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {t("upgradePro")}
          </Link>
        </CardContent>
      </Card>
    );
  }

  // PRO user view - No active room
  if (!room) {
    return (
      <Card className="col-span-12 bento-card" data-testid="translation-room-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between text-white font-['Outfit']">
            <div className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              {t("translationRooms")}
              <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                {t("events")}
              </span>
            </div>
            <button
              onClick={() => setShowConfigPanel(!showConfigPanel)}
              className={`p-2 rounded-lg transition-colors ${
                showConfigPanel 
                  ? 'text-blue-400 bg-blue-500/10' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              title={t("presenterLanguages")}
            >
              <Settings className="w-5 h-5" />
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-zinc-400 text-sm">
            {t("roomDescription")}
          </p>
          
          {/* Config panel with presenter languages */}
          {showConfigPanel && (
            <div className="p-4 bg-zinc-800/30 border border-zinc-700 rounded-lg space-y-4">
              <h4 className="text-sm font-medium text-white flex items-center gap-2">
                <Languages className="w-4 h-4 text-blue-400" />
                {t("roomConfig")}
              </h4>
              
              <PresenterLanguagesSelector
                selectedLanguages={presenterLanguages}
                onChange={setPresenterLanguages}
                label={t("presenterLanguages")}
                description={t("presenterLanguagesDescription")}
              />
              
              <p className="text-xs text-gray-500">
                {t("presenterLanguagesTip")}
              </p>
            </div>
          )}
          
          <button
            onClick={createRoom}
            disabled={isCreating || presenterLanguages.length === 0}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isCreating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <QrCode className="w-4 h-4" />
                {t("createRoom")}
              </>
            )}
          </button>
        </CardContent>
      </Card>
    );
  }

  // PRO user view - Active room
  return (
    <>
      <Card className="col-span-12 bento-card border-green-500/30" data-testid="translation-room-card-active">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between text-white font-['Outfit']">
            <div className="flex items-center gap-2">
              {connected ? (
                <Wifi className="w-5 h-5 text-green-400" />
              ) : (
                <WifiOff className="w-5 h-5 text-yellow-400" />
              )}
              Sala Ativa
              <span className="text-green-400 font-mono text-lg">{room.room_id}</span>
              <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                {connected ? "Ao vivo" : "Reconectando..."}
              </span>
            </div>
            <button
              onClick={closeRoom}
              className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              title="Encerrar sala"
            >
              <X className="w-5 h-5" />
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Viewer count and actions in a row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Viewer count */}
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800/50 rounded-lg">
              <Users className="w-5 h-5 text-blue-400" />
              <span className="text-white font-medium text-lg">{viewerCount}</span>
              <span className="text-gray-400 text-sm">
                {viewerCount === 1 ? "pessoa conectada" : "pessoas conectadas"}
              </span>
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowQRModal(true)}
                className="py-2 px-4 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
              >
                <QrCode className="w-4 h-4" />
                QR Code
              </button>
              
              <button
                onClick={copyLink}
                className="py-2 px-4 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copiar Link
              </button>
              
              <button
                onClick={shareRoom}
                className="py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                Compartilhar
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowQRModal(false)}>
          <div 
            className="bg-gray-800 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{t("shareRoom")}</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowQRModal(false)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                  title="Minimizar"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  title="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="text-center mb-4">
              <p className="text-gray-400 mb-2">{t("roomCode")}</p>
              <p className="text-4xl font-mono font-bold text-blue-400 tracking-wider">
                {room.room_id}
              </p>
            </div>
            
            {/* QR Code */}
            <div className="bg-white p-4 rounded-xl mb-4 flex items-center justify-center">
              <QRCode 
                value={getShareUrl()} 
                size={200}
                level="M"
              />
            </div>
            
            {/* URL */}
            <div className="bg-gray-900 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">{t("shareLink")}</p>
              <p className="text-sm text-blue-400 break-all font-mono">
                {getShareUrl()}
              </p>
            </div>
            
            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={copyLink}
                className="py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" />
                {t("copyLink")}
              </button>
              
              <a
                href={getShareUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {t("openLink")}
              </a>
            </div>
            
            <p className="text-center text-gray-500 text-xs mb-4">
              {t("visitorsCanScan")}
            </p>
            
            {/* End Room Button */}
            <button
              onClick={() => {
                if (window.confirm(t("endRoomConfirm"))) {
                  closeRoom();
                  setShowQRModal(false);
                }
              }}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
            >
              <X className="w-5 h-5" />
              {t("endRoom")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
